import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ShoppingBag, Phone } from 'lucide-react';
import { supabase } from '@/lib/supabase';

const mitnahmeDataFallback = {
  pizza: {
    title: 'Pizze',
    subtitle: 'Pizza zum Mitnehmen',
    items: [
      { name: 'Pizza Margherita', description: 'Mit Tomatensoße und Mozzarella', price: '8,50' },
      { name: 'Pizza Funghi Freschi', description: 'Mit Tomatensoße, Mozzarella, frischen Pilzen', price: '10,50' },
      { name: 'Pizza Prosciutto', description: 'Mit Tomatensoße, Mozzarella und Vorderschinken', price: '10,50' },
      { name: 'Pizza Salame', description: 'Mit Tomatensoße, Mozzarella, Salami', price: '10,50' },
      { name: 'Pizza Regina', description: 'Mit Tomatensoße, Mozzarella, Vorderschinken, frischen Pilzen', price: '11,50' },
      { name: 'Pizza Hawaii', description: 'Mit Tomatensoße, Mozzarella, Vorderschinken, Ananas', price: '11,50' },
      { name: 'Pizza Diavola', description: 'Mit Tomatensoße, Mozzarella, scharfe Salami, Peperoni', price: '11,50' },
      { name: 'Pizza Vegetariana', description: 'Mit Tomatensoße, Mozzarella und verschiedenem Gemüse', price: '11,50' },
      { name: 'Pizza Calzone', description: 'Mit Tomatensoße, Mozzarella, Vorderschinken, Pilzen', price: '11,50' },
      { name: 'Pizza quattro Stagioni', description: 'Mit Vorderschinken, Pilzen, Artischocken und Oliven', price: '11,50' },
      { name: 'Pizza Parma', description: 'Mit Rucola, Parmaschinken und Parmesansplittern', price: '13,50' },
      { name: 'Pizza quattro Formaggi', description: 'Mit vier verschiedenen Käsesorten', price: '12,50' },
      { name: 'Pizza Tonno e Cipolla', description: 'Mit Thunfisch und Zwiebeln', price: '12,50' },
      { name: 'Pizza Frutti di Mare', description: 'Mit Meeresfrüchten', price: '13,50' }
    ]
  },
  pasta: {
    title: 'Paste',
    subtitle: 'Pasta zum Mitnehmen',
    items: [
      { name: 'Spaghetti Napoli', description: 'Spaghetti mit Basilikum in Tomatensoße', price: '9,50' },
      { name: 'Spaghetti alla Carbonara', description: 'Mit Speck, Eigelb und Sahnesoße', price: '11,50' },
      { name: 'Spaghetti Bolognese', description: 'Spaghetti mit Hackfleischsoße', price: '11,50' },
      { name: 'Spaghetti Amatriciana', description: 'Mit Zwiebeln, Speck in Tomatensoße', price: '11,50' },
      { name: 'Spaghetti ai Frutti di Mare', description: 'Spaghetti mit Meeresfrüchten', price: '13,50' },
      { name: "Penne all'Arrabbiata", description: 'Mit Knoblauch, Chili-Schoten in Tomatensoße', price: '9,50' },
      { name: 'Penne al Salmone', description: 'Penne mit Lachs in Sahne-Soße', price: '12,50' },
      { name: 'Lasagna al Forno', description: 'Lasagna im Ofen überbacken', price: '11,50' },
      { name: 'Tortellini Panna e Prosciutto', description: 'Tortellini mit Vorderschinken in Sahnesoße', price: '11,50' },
      { name: 'Gnocchi Sorrentina', description: 'Gnocchi mit Tomatensoße und Mozzarella', price: '12,50' }
    ]
  },
  antipasti: {
    title: 'Antipasti',
    subtitle: 'Vorspeisen zum Mitnehmen',
    items: [
      { name: 'Bruschetta Classica (4 Stück)', description: 'Geröstetes Brot mit frischen Tomaten', price: '6,50' },
      { name: 'Mozzarella alla Caprese', description: 'Mozzarella mit Tomaten und Basilikum', price: '9,50' },
      { name: 'Carpaccio di Manzo', description: 'Carpaccio vom Rinderfilet mit Rucola', price: '13,50' },
      { name: 'Vitello Tonnato', description: 'Kalbfleisch mit Thunfischsoße', price: '12,50' }
    ]
  },
  salate: {
    title: 'Insalate',
    subtitle: 'Salate zum Mitnehmen',
    items: [
      { name: 'Insalata Mista', description: 'Gemischter Salat', price: '6,50' },
      { name: 'Insalata della Casa', description: 'Großer Salat mit Thunfisch, Oliven, Ei und Mais', price: '11,50' },
      { name: 'Insalata Mista Caesar', description: 'Caesar-Salat mit Hähnchen', price: '12,50' },
      { name: 'Insalata con Salmone', description: 'Gemischter Salat mit Lachs', price: '13,50' }
    ]
  },
  dessert: {
    title: 'Dolci',
    subtitle: 'Desserts zum Mitnehmen',
    items: [
      { name: 'Tiramisù', description: 'Das klassische italienische Dessert', price: '6,50' },
      { name: 'Panna Cotta', description: 'Mit Früchten', price: '5,50' },
      { name: 'Tartufo Eis', description: 'Schokoladen-Trüffeleis', price: '6,50' }
    ]
  }
};

const categories = ['pizza', 'pasta', 'antipasti', 'salate', 'dessert'];

function buildMenuFromRows(rows) {
  const result = {};
  rows.forEach((row) => {
    if (!result[row.category]) {
      result[row.category] = { title: row.category_title, subtitle: row.category_subtitle || '', items: [] };
    }
    result[row.category].items.push({ name: row.name, description: row.description || '', price: row.price });
  });
  return result;
}

export default function Mitnahmekarte() {
  const [activeCategory, setActiveCategory] = useState('pizza');
  const [mitnahmeData, setMitnahmeData] = useState(mitnahmeDataFallback);
  const [dynamicCategories, setDynamicCategories] = useState(categories);

  useEffect(() => {
    supabase
      .from('menu_items')
      .select('*')
      .eq('karte', 'mitnahme')
      .order('sort_order')
      .then(({ data }) => {
        if (data && data.length > 0) {
          setMitnahmeData(buildMenuFromRows(data));
          const cats = [...new Set(data.map((r) => r.category))];
          setDynamicCategories(cats);
          if (!cats.includes(activeCategory)) setActiveCategory(cats[0]);
        }
      });
  }, []);  // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div data-testid="mitnahmekarte-page">
      {/* Hero Section */}
      <section className="relative h-[50vh] flex items-center justify-center overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url('https://customer-assets.emergentagent.com/job_trattoria-girasole/artifacts/9mflczp9_Restaurant-Hallbergmoos-Italiener-27.jpg')`
          }}
        />
        <div className="absolute inset-0 bg-olive-900/70" />
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="relative z-10 text-center px-4"
        >
          <div className="flex items-center justify-center gap-3 mb-4">
            <ShoppingBag className="text-gold" size={28} />
            <p className="font-accent text-gold text-2xl">Da Portare Via</p>
          </div>
          <h1 className="font-heading text-5xl md:text-6xl text-cream">Mitnahmekarte</h1>
          <p className="font-body text-cream/80 mt-4 text-lg">
            Spezialpreise für unsere Abholer
          </p>
        </motion.div>
      </section>

      {/* Info Banner */}
      <section className="py-8 bg-terracotta">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-center gap-4 text-white text-center">
            <Phone size={24} />
            <p className="font-body text-lg">
              Telefonisch bestellen: <span className="font-bold">+49 (0) 811 9999888</span>
            </p>
          </div>
        </div>
      </section>

      {/* Menu Content */}
      <section className="py-16 md:py-24 bg-cream">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Category Tabs */}
          <div className="flex flex-wrap justify-center gap-2 mb-12">
            {dynamicCategories.map((category) => (
              <button
                key={category}
                data-testid={`mitnahme-tab-${category}`}
                onClick={() => setActiveCategory(category)}
                className={`px-4 py-2 md:px-6 md:py-3 font-body uppercase text-xs md:text-sm tracking-widest transition-all duration-300 ${
                  activeCategory === category
                    ? 'bg-olive-900 text-cream'
                    : 'bg-white text-olive-900 hover:bg-olive-100 border border-olive-900/20'
                }`}
              >
                {mitnahmeData[category].title}
              </button>
            ))}
          </div>

          {/* Menu Items */}
          <AnimatePresence mode="wait">
            <motion.div
              key={activeCategory}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
              className="bg-white p-8 md:p-12 shadow-[0_4px_20px_-2px_rgba(0,0,0,0.1)]"
            >
              <div className="text-center mb-10">
                <h2 className="font-heading text-3xl md:text-4xl text-olive-900 mb-2">
                  {mitnahmeData[activeCategory].title}
                </h2>
                <p className="font-body text-muted-foreground italic">
                  {mitnahmeData[activeCategory].subtitle}
                </p>
              </div>

              <div className="space-y-5">
                {mitnahmeData[activeCategory].items.map((item, index) => (
                  <motion.div
                    key={item.name}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.05 }}
                    data-testid={`mitnahme-item-${index}`}
                    className="flex justify-between items-start gap-4 pb-4 border-b border-cream-dark last:border-b-0"
                  >
                    <div className="flex-1">
                      <h3 className="font-heading text-lg md:text-xl text-olive-900 mb-1">
                        {item.name}
                      </h3>
                      <p className="font-body text-muted-foreground text-sm">
                        {item.description}
                      </p>
                    </div>
                    <div className="font-heading text-xl text-terracotta whitespace-nowrap">
                      € {String(item.price).replace(/,(\d)(?=\s|$|\/)/g, ',$10')}
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </AnimatePresence>

          {/* Hinweis */}
          <div className="mt-12 bg-olive-900 p-8 text-cream text-center">
            <h3 className="font-heading text-xl text-gold mb-4">Bestellhinweise</h3>
            <p className="font-body text-cream/80 text-sm leading-relaxed">
              Bitte bestellen Sie telefonisch vor und holen Sie Ihre Bestellung bei uns ab.
              <br />
              Die Mitnahmepreise gelten nur für Abholung, nicht für Lieferung.
            </p>
            <div className="mt-6 flex items-center justify-center gap-3">
              <Phone size={20} className="text-gold" />
              <span className="font-heading text-xl text-gold">+49 (0) 811 9999888</span>
            </div>
          </div>
        </div>
      </section>

      {/* Info Section */}
      <section className="py-12 bg-cream-dark">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="font-body text-muted-foreground text-sm leading-relaxed">
            Alle Preise in Euro inkl. MwSt. • Preise gelten nur für Abholung • 
            Änderungen vorbehalten
          </p>
        </div>
      </section>
    </div>
  );
}
